import { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import _ from 'underscore';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { useJsDataContext } from '@bingads-webui-cc-react/js-data-rest';
import { ScopeContext } from '@bingads-webui-react/scope-service';

const CCUI_PREFERENCE_KEY = 18190;

const DISPLAY_CALLOUT_QUERY = 'displaycallout';

const buildHideCouponCalloutKey = (couponId, aid) => `hide_coupon_callout_${couponId}_${aid}`;

const getDaysFromNow = (days) => {
  const date = new Date(Date.now());
  date.setDate(date.getDate() + days);

  return date;
};

export const useHideCalloutPreference = (uid, aid, coupon) => {
  const [userPreferencesLoaded, setUserPreferencesLoaded] = useState(false);
  const [hideCalloutPreference, setHideCalloutPreference] = useState(null);

  const ccJsData = useJsDataContext();
  const { scope } = useContext(ScopeContext);

  const instrumentation = useContext(InstrumentationContext);
  const { activity } = useMemo(() => instrumentation.createScenarioAndActivity({
    scenarioName: 'AutoBiddingCoupon',
    activityName: 'GetCouponPreference',
  }), [instrumentation]);

  useEffect(() => {
    if (coupon) {
      ccJsData.UserPreference
        .findAll({ uid })
        .then((data) => {
          if (data && data.length) {
            setUserPreferencesLoaded(true);
            try {
              const userPreferences = _.find(data, userPreference => userPreference.upkey === CCUI_PREFERENCE_KEY);
              if (userPreferences && userPreferences.resultAsJsonString) {
                const preferenceJson = JSON.parse(decodeURIComponent(userPreferences.resultAsJsonString));
                const hideCouponCallout = buildHideCouponCalloutKey(coupon.id, aid);
                setHideCalloutPreference(preferenceJson[hideCouponCallout] ? JSON.parse(preferenceJson[hideCouponCallout]) : null);
              }
            } catch (e) {
              if (e instanceof SyntaxError) {
                activity.trace(`getUserPreference: resultAsJsonString JSON parse failed: ${e}`);
              } else {
                throw e;
              }
            }
          }
        });
    }
  }, [activity, aid, ccJsData.UserPreference, coupon, uid]);

  const hideCalloutForEver = useCallback(() => {
    const hideCouponCalloutData = {
      couponId: coupon && coupon.id,
      aid,
      hide: true,
      endDate: null,
    };

    const hideCouponCallout = buildHideCouponCalloutKey(coupon.id, aid);

    const data = { upkey: CCUI_PREFERENCE_KEY, data: JSON.stringify({ [hideCouponCallout]: JSON.stringify(hideCouponCalloutData) }) };
    return ccJsData.UserPreference.create(data);
  }, [aid, ccJsData.UserPreference, coupon]);

  const hideCalloutFor7Days = useCallback(() => {
    const hideCouponCalloutData = {
      couponId: coupon && coupon.id,
      aid,
      hide: true,
      endDate: getDaysFromNow(7),
    };

    const hideCouponCallout = buildHideCouponCalloutKey(coupon.id, aid);

    const data = { upkey: CCUI_PREFERENCE_KEY, data: JSON.stringify({ [hideCouponCallout]: JSON.stringify(hideCouponCalloutData) }) };
    return ccJsData.UserPreference.create(data);
  }, [aid, ccJsData.UserPreference, coupon]);

  const isPreferenceSetToHidden = useMemo(
    () => hideCalloutPreference && hideCalloutPreference.hide,
    [hideCalloutPreference]
  );

  const isAfterDismissEndDate = useMemo(
    () => hideCalloutPreference && (!hideCalloutPreference.endDate || Date.parse(hideCalloutPreference.endDate) < Date.now()),
    [hideCalloutPreference]
  );

  const isDisplayCalloutQuery = useMemo(() => {
    const displayQueryCallout = scope.values[DISPLAY_CALLOUT_QUERY];
    return displayQueryCallout === 1 || displayQueryCallout === 'true';
  }, [scope.values]);

  const isCalloutVisible = useMemo(
    () => isDisplayCalloutQuery || (userPreferencesLoaded && (!isPreferenceSetToHidden || (isPreferenceSetToHidden && isAfterDismissEndDate))),
    [isAfterDismissEndDate, isPreferenceSetToHidden, isDisplayCalloutQuery, userPreferencesLoaded]
  );

  return {
    isCalloutHidden: !isCalloutVisible,
    userPreferencesLoaded,
    hideCalloutPreference,
    hideCalloutForEver,
    hideCalloutFor7Days,
  };
};
